import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { BRAND, PHOTOS, UI } from "@/content";
import { useLang } from "@/LangContext";

function Tile({ src, caption, span, testid, tall }) {
  return (
    <motion.figure
      data-testid={testid}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative overflow-hidden rounded-2xl border border-gold/20 ${span}`}
    >
      <img
        src={src}
        alt={caption}
        loading="lazy"
        className={`w-full ${tall ? "h-72 sm:h-[420px]" : "h-64 sm:h-80"} object-cover [transition-duration:1200ms] ease-out group-hover:scale-[1.05]`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <figcaption className="absolute bottom-4 left-5 right-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-goldbright">
          {caption}
        </span>
      </figcaption>
    </motion.figure>
  );
}

export default function Gallery() {
  const { lang } = useLang();
  const t = UI[lang] || UI.es;
  const c = t.gallery.captions;

  return (
    <section
      id="galeria"
      className="relative py-24 lg:py-28 bg-pine scroll-mt-16"
      data-testid="gallery-section"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <p className="font-mono text-[10px] sm:text-xs tracking-[0.3em] uppercase text-goldbright flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-9 bg-gold/70 inline-block" />
            {t.gallery.eyebrow}
            <span className="h-px w-9 bg-gold/70 inline-block" />
          </p>
          <h2 className="font-serif font-medium text-4xl sm:text-5xl text-cream leading-[1.08]">
            {t.gallery.title}
          </h2>
          <p className="mt-5 text-sage font-light leading-relaxed">{t.gallery.sub}</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-12 gap-5">
          <Tile
            src={PHOTOS.storefront}
            caption={c.storefront}
            span="sm:col-span-7"
            tall
            testid="gallery-item-storefront"
          />
          <Tile
            src={PHOTOS.lounge}
            caption={c.lounge}
            span="sm:col-span-5"
            tall
            testid="gallery-item-lounge"
          />
          <Tile
            src={PHOTOS.tea}
            caption={c.tea}
            span="sm:col-span-4"
            testid="gallery-item-tea"
          />
          <Tile
            src={PHOTOS.hall}
            caption={c.hall}
            span="sm:col-span-4"
            testid="gallery-item-hall"
          />
          <motion.a
            href={BRAND.fresha}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-cta-tile"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="sm:col-span-4 group relative h-64 sm:h-80 rounded-2xl bg-moss border border-gold/25 overflow-hidden flex flex-col items-center justify-center gap-5 hover:border-gold/60 transition-colors duration-500"
          >
            <span className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(184,157,98,0.2),transparent_60%)]" />
            <MapPin className="w-6 h-6 text-gold" />
            <p className="font-serif italic text-2xl text-cream text-center px-8 leading-snug">
              {c.cta}
            </p>
            <span className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-medium text-forest group-hover:scale-105 transition-transform duration-300">
              {c.ctaBtn}
              <ArrowUpRight className="w-4 h-4" />
            </span>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
